/**
 * "Your role" — shown once, right after a successor or keyholder accepts an
 * invitation.
 *
 * Before this screen, acceptance dropped the person on their own empty
 * dashboard, as if they were a primary user, and nothing said that the
 * primary's dashboard was one tap away. This screen says what the role
 * means in plain words: what they can see now (whatever the primary has
 * unlocked for them), what stays sealed until an end-of-life event is
 * verified, and the one thing worth doing now — setting a secret code, so
 * encrypted recordings and files can open for them later. Its main button
 * opens the primary's dashboard in viewing mode.
 */
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, SafeAreaView, TouchableOpacity, Image, ScrollView } from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { useUserSelection } from '../../contexts/UserSelectionContext';
import { isWrappedMasterKeyEnrolled } from '../../services/encryption/MasterKeyService';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('TrustedPartyWelcome');

type Role = 'successor' | 'keyholder';

type WelcomeRouteProp = RouteProp<
  { params: { primaryName: string; role: Role; primaryUserId?: string } },
  'params'
>;

const COPY: Record<
  Role,
  { title: (n: string) => string; intro: (n: string) => string; now: (n: string) => string; later: (n: string) => string }
> = {
  successor: {
    title: (n) => `You're ${n}'s successor`,
    intro: (n) => `${n} chose you to carry out their wishes. Here is what that means.`,
    now: (n) =>
      `Anything ${n} has unlocked for you is already there, on their dashboard. It updates as they add more.`,
    later: () =>
      'Locked information stays sealed until an end-of-life event is verified — by the text-message check or a death certificate. Then it opens to you.',
  },
  keyholder: {
    title: (n) => `You're ${n}'s keyholder`,
    intro: (n) => `${n} trusts you to confirm an end-of-life event when the time comes. Here is what that means.`,
    now: (n) =>
      `Anything ${n} has unlocked for you is on their dashboard. Keyholders never see sensitive categories such as passwords and financial details.`,
    later: (n) =>
      `When the time comes, you confirm the event so ${n}'s successor can be given access. Nothing happens without you.`,
  },
};

export const TrustedPartyWelcomeScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<WelcomeRouteProp>();
  const { primaryName, role, primaryUserId } = route.params;
  const { currentUserId, availablePrimaryUsers, setSelectedUserId, refreshPrimaryUsers } =
    useUserSelection();
  const [needsSecretCode, setNeedsSecretCode] = useState(false);

  const copy = COPY[role] ?? COPY.successor;
  const name = primaryName || 'They';

  useEffect(() => {
    let cancelled = false;
    isWrappedMasterKeyEnrolled()
      .then((enrolled) => {
        if (!cancelled) setNeedsSecretCode(!enrolled);
      })
      .catch((error) => logger.debug('Enrollment check failed:', error));
    // The list of primaries is refreshed here as well as on acceptance, so
    // a reload or a deep link still finds the new connection.
    refreshPrimaryUsers().catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolvePrimaryId = (): string | undefined => {
    if (primaryUserId) return primaryUserId;
    const byName = availablePrimaryUsers.find((p) => p.name === primaryName && p.role === role);
    return (byName ?? availablePrimaryUsers[0])?.id;
  };

  const openPrimaryDashboard = () => {
    const id = resolvePrimaryId();
    if (id) setSelectedUserId(id);
    else logger.warn('No primary user to select yet; opening own dashboard');
    navigation.reset({ index: 0, routes: [{ name: 'Dashboard' }] });
  };

  const openOwnDashboard = () => {
    if (currentUserId) setSelectedUserId(currentUserId);
    navigation.reset({ index: 0, routes: [{ name: 'Dashboard' }] });
  };

  const setSecretCode = () => {
    navigation.navigate('Profile', { screen: 'SetRecoveryCodeIntro' });
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <Image source={require('../../assets/icons/check.png')} style={styles.headerIcon} />
        <Text style={styles.title}>{copy.title(name)}</Text>
        <Text style={styles.intro}>{copy.intro(name)}</Text>
      </View>

      <View style={styles.sheet}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <View style={styles.card}>
            <Text style={styles.cardLabel}>What you can see now</Text>
            <Text style={styles.cardText}>{copy.now(name)}</Text>
          </View>

          <View style={styles.card}>
            <Text style={styles.cardLabel}>What stays sealed</Text>
            <Text style={styles.cardText}>{copy.later(name)}</Text>
          </View>

          {needsSecretCode && (
            <View style={[styles.card, styles.cardTask]}>
              <Text style={styles.cardLabel}>One thing to do</Text>
              <Text style={styles.cardText}>
                Set your secret code. It protects your own account, and it is what lets
                encrypted recordings and files open for you when they are shared.
              </Text>
              <TouchableOpacity
                style={styles.taskButton}
                onPress={setSecretCode}
                accessibilityRole="button"
                accessibilityLabel="Set my secret code"
              >
                <Text style={styles.taskButtonText}>Set my secret code</Text>
              </TouchableOpacity>
            </View>
          )}

          <TouchableOpacity
            style={styles.primaryButton}
            onPress={openPrimaryDashboard}
            accessibilityRole="button"
            accessibilityLabel={`See ${name}'s dashboard`}
          >
            <Text style={styles.primaryButtonText}>See {name}'s dashboard</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.link}
            onPress={openOwnDashboard}
            accessibilityRole="button"
            accessibilityLabel="Explore FinalKey for myself"
          >
            <Text style={styles.linkText}>Explore FinalKey for myself</Text>
          </TouchableOpacity>
          <Text style={styles.footnote}>
            You can switch between dashboards any time from the top of the Dashboard screen.
          </Text>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    alignItems: 'center',
    paddingHorizontal: ms(24),
    paddingTop: ms(24),
    paddingBottom: ms(20),
  },
  headerIcon: {
    width: s(44),
    height: s(44),
    tintColor: '#FFFFFF',
    marginBottom: ms(10),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: ms(6),
  },
  intro: {
    fontSize: ms(15, 0.3),
    lineHeight: ms(22, 0.3),
    color: 'rgba(255,255,255,0.92)',
    textAlign: 'center',
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
    paddingHorizontal: ms(16),
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(12),
  },
  cardTask: {
    borderColor: '#29BFC0',
    borderWidth: ms(2),
  },
  cardLabel: {
    fontSize: ms(12, 0.3),
    fontWeight: '700',
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: '#1D5C59',
    marginBottom: ms(6),
  },
  cardText: {
    fontSize: ms(14.5, 0.3),
    lineHeight: ms(21, 0.3),
    color: '#1C2C4F',
  },
  taskButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#DFF2F1',
    borderRadius: ms(25),
    paddingVertical: ms(10),
    paddingHorizontal: ms(16),
    marginTop: ms(12),
  },
  taskButtonText: {
    color: '#1D5C59',
    fontSize: ms(14, 0.3),
    fontWeight: '700',
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    alignItems: 'center',
    marginTop: ms(6),
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
  link: {
    alignItems: 'center',
    paddingVertical: ms(13),
  },
  linkText: {
    color: '#1C2C4F',
    fontSize: ms(14, 0.3),
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  footnote: {
    fontSize: ms(12, 0.3),
    lineHeight: ms(17, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  bottomPadding: {
    height: s(40),
  },
});

export default TrustedPartyWelcomeScreen;
